import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/SEOHead';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { HelpCircle, Users, Building, Gavel, Shield, MessageCircle } from 'lucide-react';

const FAQPage = () => {
  const seoConfig = {
    title: 'FAQ - Domande Frequenti | WEQASA',
    description: 'Trova le risposte alle domande più comuni su WEQASA: come vendere casa velocemente, partecipare alle gare di vendita e acquistare al miglior prezzo.',
    keywords: 'faq weqasa, domande frequenti vendita casa, come funziona gara vendita immobiliare'
  };

  const faqCategories = [
    {
      icon: Users,
      title: 'Per gli Acquirenti',
      faqs: [
        {
          question: 'Come posso partecipare a una gara di vendita?',
          answer: 'Per partecipare a una gara di vendita devi prima registrarti sulla piattaforma, selezionare l\'immobile di interesse e richiedere la partecipazione. Dovrai caricare i documenti richiesti e versare una caparra di garanzia. Una volta approvato dall\'agente, sarai abilitato a fare offerte.'
        },
        {
          question: 'Quanto costa la caparra?',
          answer: 'L\'importo della caparra varia per ogni immobile ed è indicato nella scheda dell\'immobile. Generalmente corrisponde al 5-10% del prezzo base. La caparra viene restituita se non ti aggiudichi l\'immobile.'
        },
        {
          question: 'Come funziona il rilancio minimo?',
          answer: 'Ogni nuova offerta deve superare la precedente di almeno l\'importo del rilancio minimo, che è indicato nella scheda dell\'immobile. Ad esempio, se il rilancio minimo è di €1.000 e l\'offerta attuale è di €200.000, la prossima offerta valida deve essere di almeno €201.000.'
        },
        {
          question: 'Cosa succede se vinco la gara di vendita?',
          answer: 'Se la tua offerta è la più alta e supera il prezzo minimo stabilito dal venditore, ti aggiudichi l\'immobile. Verrai contattato dall\'agente per procedere con la formalizzazione dell\'acquisto. La caparra versata verrà scalata dal prezzo finale.'
        },
        {
          question: 'Posso visitare l\'immobile prima della gara?',
          answer: 'Assolutamente sì! Consigliamo sempre di visitare l\'immobile prima di partecipare alla gara di vendita. Puoi prenotare una visita direttamente dalla scheda dell\'immobile.'
        }
      ]
    },
    {
      icon: Building,
      title: 'Per i Venditori',
      faqs: [
        {
          question: 'Come faccio a vendere il mio immobile su WEQASA?',
          answer: 'Registrati come proprietario e inserisci i dati del tuo immobile. Un agente verificato della nostra rete prenderà in carico la tua richiesta, effettuerà una valutazione e ti proporrà un mandato di vendita.'
        },
        {
          question: 'Quanto costa vendere su WEQASA?',
          answer: 'L\'inserimento dell\'immobile è gratuito. Le commissioni vengono applicate solo a vendita conclusa e sono sempre trasparenti e concordate in anticipo nel mandato.'
        },
        {
          question: 'Posso impostare un prezzo minimo?',
          answer: 'Sì, puoi concordare con l\'agente un prezzo minimo di vendita (riservato). Se l\'offerta finale non raggiunge questo prezzo, non sei obbligato a vendere.'
        },
        {
          question: 'Quanto tempo ci vuole per vendere?',
          answer: 'I tempi variano in base all\'immobile e al mercato. Grazie al sistema di gara competitiva, mediamente i tempi di vendita sono più brevi rispetto ai metodi tradizionali.'
        }
      ]
    },
    {
      icon: Gavel,
      title: 'Sul Processo di Gara',
      faqs: [
        {
          question: 'Quanto dura una gara di vendita?',
          answer: 'La durata della gara è stabilita dall\'agente e può variare. Generalmente le gare durano da 15 minuti a qualche ora. La durata è sempre indicata nella scheda dell\'immobile.'
        },
        {
          question: 'Cosa succede se faccio un\'offerta negli ultimi secondi?',
          answer: 'Per evitare il "sniping" (offerte dell\'ultimo secondo), alcune gare prevedono un\'estensione automatica del tempo se viene fatta un\'offerta negli ultimi minuti.'
        },
        {
          question: 'Le offerte sono anonime?',
          answer: 'Sì, durante la gara le offerte sono anonime. Gli altri partecipanti vedono solo l\'importo dell\'offerta, non chi l\'ha fatta.'
        },
        {
          question: 'Cosa succede se la gara non raggiunge il prezzo minimo?',
          answer: 'Se l\'offerta più alta non raggiunge il prezzo minimo stabilito dal venditore, la gara si conclude senza aggiudicazione. Il venditore può decidere di accettare comunque l\'offerta o rimettere l\'immobile in vendita.'
        }
      ]
    },
    {
      icon: Shield,
      title: 'Pagamenti e Sicurezza',
      faqs: [
        {
          question: 'I miei dati sono al sicuro?',
          answer: 'Sì, utilizziamo i più alti standard di sicurezza per proteggere i tuoi dati personali e finanziari. Tutte le comunicazioni sono crittografate e siamo conformi al GDPR.'
        },
        {
          question: 'Come funzionano i pagamenti?',
          answer: 'I pagamenti vengono gestiti tramite Stripe, uno dei sistemi di pagamento più sicuri al mondo. Le caparre sono trattenute in modo sicuro fino alla conclusione della transazione.'
        },
        {
          question: 'Cosa succede alla caparra se non vinco?',
          answer: 'Se non ti aggiudichi l\'immobile, la caparra ti viene restituita integralmente entro pochi giorni lavorativi dalla conclusione della gara.'
        },
        {
          question: 'Posso annullare la mia partecipazione?',
          answer: 'Puoi ritirarti dalla gara prima che questa inizi. Una volta iniziata la gara, le offerte fatte sono vincolanti. Ti consigliamo di leggere attentamente il regolamento prima di partecipare.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead {...seoConfig} />
      <Header />

      {/* Hero Section - Apple Style */}
      <section className="bg-[#F5F5F7] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-[#0071e3]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8 text-[#0071e3]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1D1D1F] tracking-tight mb-4">
            Domande Frequenti
          </h1>
          <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
            Trova le risposte alle domande più comuni su WEQASA e il processo di vendita immobiliare.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6">
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-16 last:mb-0">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-[#0071e3]/10 rounded-xl flex items-center justify-center">
                  <category.icon className="w-5 h-5 text-[#0071e3]" />
                </div>
                <h2 className="text-2xl font-semibold text-[#1D1D1F]">
                  {category.title}
                </h2>
              </div>
              <Accordion type="single" collapsible className="space-y-3">
                {category.faqs.map((faq, faqIndex) => (
                  <AccordionItem 
                    key={faqIndex} 
                    value={`${categoryIndex}-${faqIndex}`}
                    className="bg-[#F5F5F7] border-0 rounded-xl px-6 data-[state=open]:bg-white data-[state=open]:shadow-sm data-[state=open]:ring-1 data-[state=open]:ring-black/5"
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-5">
                      <span className="font-medium text-[#1D1D1F] text-[15px] pr-4">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-[#424245] text-[15px] leading-relaxed pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-14 h-14 bg-[#0071e3]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <MessageCircle className="w-7 h-7 text-[#0071e3]" />
          </div>
          <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-3">
            Non hai trovato la risposta che cercavi?
          </h3>
          <p className="text-[#86868B] mb-8 max-w-lg mx-auto">
            Il nostro team è sempre disponibile per aiutarti. Contattaci e ti risponderemo il prima possibile.
          </p>
          <Link 
            to="/contatti" 
            className="inline-flex items-center px-8 py-4 bg-[#0071e3] text-white font-medium rounded-full hover:bg-[#0077ED] transition-colors"
          >
            Contattaci
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
